'use client';

import { fetchMarketingProductList } from '@/api/marketingPages';
import type { IProductListItem } from '@/api/types/product';
import type { ListProduct, WishProduct } from '@/api/types/public';
import Image from '@/components/Image';
import Wish from '@/components/Product/Wish';
import { useAppNavigation } from '@/hooks/useAppNavigation';
import { useTranslations } from '@/i18n/use-translations';
import { useStore } from '@/providers/app-store-provider';
import { formatPriceNumberWithSymbol, priceInfo as getPriceInfo } from '@/utils/price';
import { EPlatform } from '@/utils/enums/platform-enum';
import { StarFilled } from '@zeelool/icon';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { useUserIdentityChange } from '../../../hooks/useUserIdentityChange';

interface PromotionProductSectionProps {
  pageId: number;
  titleImage?: string;
  titleAlt?: string;
  groupId: string;
  products: IProductListItem[];
  deviceType: number;
  limit: number;
  initialHasMore: boolean;
  adp?: string;
  refreshOnMount?: boolean;
  onProductClickTrack: (item: IProductListItem, index: number) => void;
  onProductExposureTrack: (item: IProductListItem, index: number) => void;
}

const FALLBACK_CURRENCY = {
  name: 'USD',
  full_symbol: '$',
  symbol: '$',
  exchange_rate: '1.000000',
  symbol_prefix: '$',
  symbol_suffix: '',
} as const;

type WishProductData = (ListProduct | WishProduct) & {
  business_sku?: string;
  sku?: string;
};

const ExposureDetector = memo(function ExposureDetector({
  item,
  index,
  onExposure,
}: {
  item: IProductListItem;
  index: number;
  onExposure: (item: IProductListItem, index: number) => void;
}) {
  const { ref, inView } = useInView({ triggerOnce: true, threshold: 0.8 });

  useEffect(() => {
    if (inView && item.id) {
      onExposure(item, index);
    }
  }, [inView, item, index, onExposure]);

  return <div ref={ref} className="pointer-events-none absolute inset-0" />;
});

const ProductItem = memo(function ProductItem({
  item,
  index,
  isWap,
  currency,
  userInfo,
  onProductClick,
}: {
  item: IProductListItem;
  index: number;
  isWap: boolean;
  currency: unknown;
  userInfo: unknown;
  onProductClick: (item: IProductListItem, index: number) => void;
}) {
  const priceData = getPriceInfo(userInfo, item, currency as never) as {
    price?: string;
    sell_price?: string;
  };
  const isSoldOut = Number(item.stock) <= 0;
  const t = useTranslations('product');

  return (
    <div className="relative rounded-[8px] bg-white lg:rounded-[10px] 2xl:rounded-[12px]">
      <a
        href={item.shop_sku ? `/goods-detail/${item.shop_sku}` : undefined}
        className={`box-border flex h-full w-full flex-col justify-between ${
          isWap
            ? 'px-[12px] pb-[12px] pt-[36px]'
            : 'px-[24px] pb-[24px] pt-[50px] lg:px-[28px] lg:pb-[28px] xl:px-[30px] 2xl:px-[32px]'
        }`}
        onClick={(event) => {
          event.preventDefault();
          onProductClick(item, index);
        }}
      >
        <Wish
          data={item as WishProductData}
          className="absolute right-[12px] top-[12px] z-[2] leading-[24px] text-auxiliary-dark-gray"
          wishNumClassName="ml-2 text-14"
          iconClassName={isWap ? 'text-[20px]' : 'text-[22px]'}
          iconSize={isWap ? '20px' : '22px'}
          showWishNum={false}
          loginSituation="促销活动页收藏"
          collectScene="促销活动页"
          price={priceData.sell_price || priceData.price || ''}
        />

        <div className="flex flex-1 items-center justify-center">
          <div
            className={`m-auto box-border w-full ${
              isWap ? 'px-[15px] pb-[26px]' : 'mb-[30px] mt-[40px] px-[14px] pb-[14px] lg:px-[20px]'
            }`}
          >
            <div className="relative aspect-[2/1] w-full">
              <Image
                src={item?.image?.product?.[0] || ''}
                alt={item.name || item.short_name || item.shop_sku || ''}
                width={300}
                height={150}
                className="h-full w-full object-cover"
              />
              {isSoldOut && (
                <span
                  className={`pointer-events-none absolute left-1/2 top-1/2 z-[2] flex aspect-square -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-black/60 text-center ${isWap ? 'h-[66px] w-[66px]' : 'h-[5.729vw] max-h-[110px] w-[5.729vw] max-w-[110px]'}`}
                >
                  <div
                    className={`whitespace-pre-wrap text-white ${
                      isWap
                        ? 'text-[11px] leading-[13px]'
                        : 'text-[clamp(12px,0.885vw,17px)] leading-[clamp(14px,1.042vw,20px)]'
                    }`}
                    dangerouslySetInnerHTML={{ __html: t('soldOut.soldOut') }}
                  />
                </span>
              )}
            </div>
          </div>
        </div>

        <div>
          <div className="mb-[3px] flex items-center justify-between gap-[6px] lg:mb-[4px] 2xl:mb-[5px]">
            <span
              className={`line-clamp-1 flex-1 text-[#808080] ${
                isWap ? 'text-[12px]' : 'text-[16px] lg:text-[18px] xl:text-[20px]'
              }`}
            >
              {item.short_name || item.name || item.shop_sku}
            </span>
            <span className="flex items-center gap-[2px] text-[#282828]">
              <StarFilled className={isWap ? 'text-[11px]' : 'text-[13px]'} />
              <span className={isWap ? 'text-[12px]' : 'text-[14px]'}>
                {Number(item.spu_review_stars || 0).toFixed(1)}
              </span>
            </span>
          </div>

          <div className="flex items-end justify-between">
            <p className="flex flex-1 flex-wrap items-center gap-y-[4px]">
              <span className="flex min-w-0 flex-wrap items-center">
                <span
                  className={`notranslate mr-[6px] font-bb text-[#282828] lg:mr-[8px] ${
                    isWap ? 'text-[14px]' : 'text-[16px] leading-[19px]'
                  }`}
                >
                  {formatPriceNumberWithSymbol(priceData.sell_price || '', currency as never)}
                </span>
                {priceData.price !== priceData.sell_price && (
                  <span
                    className={`notranslate text-[#a9a9a9] line-through ${
                      isWap ? 'text-[12px]' : 'text-[14px]'
                    }`}
                  >
                    {formatPriceNumberWithSymbol(priceData.price || '', currency as never)}
                  </span>
                )}
              </span>
            </p>
          </div>
        </div>

        {isSoldOut && (
          <div className="pointer-events-none absolute inset-0 z-[1] rounded-[8px] bg-white/40 lg:rounded-[10px] 2xl:rounded-[12px]" />
        )}
      </a>
    </div>
  );
});

export default function PromotionProductSection({
  pageId,
  titleImage,
  titleAlt,
  groupId,
  products,
  deviceType,
  limit,
  initialHasMore,
  adp,
  refreshOnMount = false,
  onProductClickTrack,
  onProductExposureTrack,
}: PromotionProductSectionProps) {
  const isWap = deviceType !== EPlatform.WEB;
  const { navigate } = useAppNavigation();
  const t = useTranslations('activity.flashSale');
  const currency = useStore((state) => state.currency) || FALLBACK_CURRENCY;
  const userInfo = useStore((state) => state.userInfo);
  const latestProductRequestIdRef = useRef(0);
  const [baseList, setBaseList] = useState<IProductListItem[]>(products);
  const [moreList, setMoreList] = useState<IProductListItem[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [hasMore, setHasMore] = useState(initialHasMore);

  const productList = useMemo(() => [...baseList, ...moreList], [baseList, moreList]);

  const handleProductClick = useCallback(
    (item: IProductListItem, index: number) => {
      onProductClickTrack(item, index);
      if (item.shop_sku) {
        navigate(`/goods-detail/${item.shop_sku}`);
      }
    },
    [navigate, onProductClickTrack],
  );

  const handleViewMore = useCallback(async () => {
    if (isLoading || !groupId) return;

    const requestId = latestProductRequestIdRef.current + 1;
    latestProductRequestIdRef.current = requestId;
    const isLatestRequest = () => latestProductRequestIdRef.current === requestId;

    setIsLoading(true);
    try {
      const nextPage = currentPage + 1;
      const response = await fetchMarketingProductList({
        groupId: Number(groupId),
        page: nextPage,
        pageSize: limit,
        pageId,
        adp,
        in_stock: 3,
      });
      if (!isLatestRequest()) return;

      const nextList = (response?.items || []) as IProductListItem[];

      setMoreList((prev) => [...prev, ...nextList]);
      setCurrentPage(nextPage);
      setHasMore(Boolean(response?.has_more));
    } finally {
      if (isLatestRequest()) {
        setIsLoading(false);
      }
    }
  }, [adp, currentPage, groupId, isLoading, limit, pageId]);

  const refreshProductsForIdentityChange = useCallback(async () => {
    if (!groupId) return;

    const requestId = latestProductRequestIdRef.current + 1;
    latestProductRequestIdRef.current = requestId;
    const isLatestRequest = () => latestProductRequestIdRef.current === requestId;

    setIsLoading(true);
    try {
      const response = await fetchMarketingProductList({
        groupId: Number(groupId),
        page: 1,
        pageSize: limit,
        pageId,
        adp,
        in_stock: 3,
      });
      if (!isLatestRequest()) return;

      setBaseList((response?.items || []) as IProductListItem[]);
      setMoreList([]);
      setCurrentPage(1);
      setHasMore(Boolean(response?.has_more));
    } finally {
      if (isLatestRequest()) {
        setIsLoading(false);
      }
    }
  }, [adp, groupId, limit, pageId]);

  useUserIdentityChange(refreshProductsForIdentityChange, true, {
    refreshOnEnable: refreshOnMount,
  });

  if (productList.length === 0) {
    return null;
  }

  return (
    <section
      className={`bg-[#f4f4f4] ${isWap ? 'px-[12px] pt-[24px] pb-[32px]' : 'px-[48px] pt-[10px] pb-[48px]'}`}
    >
      <div className={isWap ? 'mb-[18px]' : 'mb-[2px]'}>
        {titleImage ? (
          <Image
            src={titleImage}
            alt={titleAlt || ''}
            width={1920}
            height={126}
            className="mx-auto h-auto w-full"
          />
        ) : (
          <h2
            className={`text-center font-bb text-primary-black ${
              isWap ? 'text-32 leading-[38px]' : 'text-44 leading-[54px]'
            }`}
          >
            {titleAlt}
          </h2>
        )}
      </div>

      <div className={`grid ${isWap ? 'grid-cols-2 gap-[10px]' : 'grid-cols-4 gap-[16px]'}`}>
        {productList.map((item, index) => (
          <div key={`${item.id || item.shop_sku}-${index}`} className="relative">
            <ProductItem
              item={item}
              index={index}
              isWap={isWap}
              currency={currency}
              userInfo={userInfo}
              onProductClick={handleProductClick}
            />
            <ExposureDetector item={item} index={index} onExposure={onProductExposureTrack} />
          </div>
        ))}
      </div>

      {hasMore && (
        <div className={`flex justify-center ${isWap ? 'mt-[24px]' : 'mt-[56px]'}`}>
          <button
            type="button"
            className={`flex items-center justify-center rounded-[999px] border border-solid border-auxiliary-dark-gray bg-transparent font-bb text-primary-black transition-opacity ${
              isWap ? 'h-[43px] w-[282px] max-w-full text-[14px]' : 'h-[46px] w-[330px] text-16'
            } ${isLoading ? 'cursor-not-allowed opacity-50' : 'cursor-pointer hover:opacity-80'}`}
            onClick={handleViewMore}
            disabled={isLoading}
          >
            {isLoading ? t('loading') : t('viewMore')}
          </button>
        </div>
      )}
    </section>
  );
}
